import { ModalCountContext } from 'components/modals/ModalProvider';
import useAuth from 'hooks/useAuth';
import { LayoutGroup } from 'motion/react';
import { Fragment, ReactNode, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Location, Params, parsePath, To, useLocation, useNavigate } from 'react-router-dom';
import { NOT_FOUND_PATH } from 'routes/constants';
import { UserData } from 'types/user';
import { captureCreateDialogClosed, captureCreateDialogOpened, CreateDialogOutcome } from 'utils/formAnalytics';
import { setActiveCreation } from './activeCreation';
import {
    CreateDialogFinishOptions,
    CreateDialogMinimisedProvider,
    CreateDialogOpenProvider,
    CreateDialogOpener,
    CreateDialogOpenerProvider,
    CreateDialogRequest,
    CreateDialogRequestProvider,
    CreateDialogSavedListProvider,
} from './CreateDialogContext';
import { CreateDialogFrame } from './CreateDialogFrame';
import { CreateDialogQuestion } from './CreateDialogQuestion';
import { focusFirstEmptyRequired } from './focusFirstEmptyRequired';
import type { CreateRecordType } from './recordTypes';
import { CreateRoute, matchCreateRoute } from './registry';
import { SavedForLaterList } from './SavedForLaterList';
// After the registry: the tab brings the export ribbon in, which the layout loads after every create page.
import { CreateDialogTab } from './CreateDialogTab';

type ActiveRequest = {
    id: number;
    route: CreateRoute;
    location: Location;
    params: Params<string>;
    /** How many creations this request has finished and been started again after. */
    round: number;
    /** Shown as a tab on the bottom edge, with its form mounted and hidden. */
    minimised: boolean;
    /** Another for each creation of the request, which tells a save of the last one that answers late from this one. */
    entryId: string;
};

/** A creation that is to open: its route, and the create URL with its opener's state. */
type Start = Pick<ActiveRequest, 'route' | 'params'> & {
    to: Pick<Location, 'pathname' | 'search' | 'hash' | 'state'>;
};

/** What the user is asked about the creation that exists: as it closes, or as `next` waits to take its place. */
type Question = { next: Start | null };

/** One creation of a request, from its form mounting to it finishing: where it finished, once it has. */
type Attempt = { target: To | null };

/** The creation the dialog's events are about: its form, and when it last opened; `null` while it is minimised. */
type Shown = { form: CreateRecordType; openedAt: number | null };

function captureClosed(shown: Shown | null, outcome: CreateDialogOutcome) {
    if (shown) {
        const secondsOpen = shown.openedAt === null ? 0 : Math.round((Date.now() - shown.openedAt) / 1000);
        captureCreateDialogClosed({ form: shown.form, outcome, secondsOpen });
    }
}

function isRecordId(value: string | undefined): boolean {
    const id = Number(value);
    return Number.isInteger(id) && id > 0;
}

function sourceIdOf({ route, params }: ActiveRequest): number | null {
    return route.sourceParam ? Number(params[route.sourceParam]) : null;
}

/** What the route's guards check on its page: a permission, a feature flag, ids that are numbers. */
function mayOpen(
    { permissions, requiredFeature, numericParams = [] }: CreateRoute,
    params: Params<string>,
    user: Pick<UserData, 'permissions' | 'enabledFeatures'> | null | undefined
): boolean {
    const hasPermission = !permissions || !!user?.permissions?.some(({ code }) => permissions.includes(code));
    const hasFeature = !requiredFeature || !!user?.enabledFeatures?.includes(requiredFeature);
    return hasPermission && hasFeature && numericParams.every((name) => isRecordId(params[name]));
}

/**
 * The first thing in the dialog's scrolling body. When the request is started again after a creation, the body
 * goes back to its top and the first required field left empty takes the focus.
 */
function BodyTop({ round }: { round: number }) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const body = ref.current?.parentElement;
        if (round === 0 || !body) {
            return undefined;
        }
        body.scrollTop = 0;
        // The form draws its fields in its own time: at once from what is cached, after a loader otherwise.
        const observer = new MutationObserver(() => {
            if (focusFirstEmptyRequired(body)) {
                observer.disconnect();
            }
        });
        // On a timer, so the defaults a form sets once it has mounted are in before a field is picked.
        const timer = setTimeout(() => {
            if (!focusFirstEmptyRequired(body)) {
                observer.observe(body, { childList: true, subtree: true });
            }
        });
        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [round]);

    return <div ref={ref} />;
}

/**
 * The create dialog's one home, in the new design's layout: everything under it can open a creation over the
 * page it is on, and the dialog outlives the page changing behind it. One creation exists at a time, open or
 * minimised to a tab that follows the user from page to page. A creation that finishes closes the dialog, or
 * starts the same request again while Create another is on.
 *
 * A creation nothing was typed in closes, and gives way to the next one, without a word. Once something was
 * typed the user is asked: closing it saves it as a draft, where the form offers that, or discards it, and so does
 * opening another, which then opens.
 *
 * The Saved for later list, the drafts the user sees, is held here too, for the sidebar's row.
 */
export function CreateDialogHost({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const page = useLocation();
    // The page as of now, for what runs later than the render it was made in: a form finishes from the render it
    // was submitted in, and the user may have minimised the dialog and moved on since.
    const pageRef = useRef(page);
    const popups = useContext(ModalCountContext);
    const [active, setActive] = useState<ActiveRequest | null>(null);
    const [question, setQuestion] = useState<Question | null>(null);
    const [showsSaved, setShowsSaved] = useState(false);
    const [createAnother, setCreateAnother] = useState(false);
    const layoutGroupId = useId();
    const lastIdRef = useRef(0);
    // Whether a dialog is open as of the last open, close, finish, minimise or reopen, ahead of the render that
    // shows it. Every `setActive` sets it too. A minimised dialog is not open: what its form opens then belongs
    // to the page.
    const isOpenRef = useRef(false);
    // The request of the dialog that exists, as its provider last rendered it; `null` while there is none.
    const requestRef = useRef<CreateDialogRequest>(null);
    // The page the dialog was over as it was first minimised, after which the user moves about; `null` for a
    // dialog that never was, which is still over the page it was opened over.
    const pageBehindRef = useRef<string | null>(null);
    const isOpen = useCallback(() => isOpenRef.current, []);
    // Read as a form finishes, from a closure that may be older than the last change of the switch.
    const createAnotherRef = useRef(false);
    const attemptRef = useRef<Attempt>({ target: null });
    const entryIdRef = useRef('');
    // Whether a question is up, as of the event under way.
    const askingRef = useRef(false);
    // `null` while no creation exists, ahead of the render, as `isOpenRef` is.
    const shownRef = useRef<Shown | null>(null);

    const showSaved = useCallback(() => setShowsSaved(true), []);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    // Lowered a render after the answer, not with it: the Escape that answers reaches the dialog as well, in
    // either order, and must not ask again.
    useEffect(() => {
        askingRef.current = question !== null;
    }, [question]);

    const ask = useCallback((next: Start | null) => {
        askingRef.current = true;
        setQuestion({ next });
    }, []);

    const changeCreateAnother = useCallback((on: boolean) => {
        createAnotherRef.current = on;
        setCreateAnother(on);
    }, []);

    const minimise = useCallback(() => {
        isOpenRef.current = false;
        pageBehindRef.current ??= page.pathname;
        if (shownRef.current?.openedAt != null) {
            captureClosed(shownRef.current, 'minimised');
            shownRef.current.openedAt = null;
        }
        setActive((current) => current && { ...current, minimised: true });
    }, [page.pathname]);

    const reopen = useCallback(() => {
        isOpenRef.current = true;
        // Keep editing lands here too, on a dialog that may never have been minimised: that one did not open again.
        if (shownRef.current?.openedAt === null) {
            captureCreateDialogOpened(shownRef.current.form);
            shownRef.current.openedAt = Date.now();
        }
        setActive((current) => current && { ...current, minimised: false });
    }, []);

    /** Ends the creation that exists, if one does, and what was typed in it with it. */
    const end = useCallback(() => {
        captureClosed(shownRef.current, 'discarded');
        shownRef.current = null;
        isOpenRef.current = false;
        // Gone as of this call, ahead of the render that unmounts it: an `open` right after starts afresh.
        requestRef.current = null;
        setActive(null);
    }, []);

    const start = useCallback(
        ({ route, params, to }: Start) => {
            lastIdRef.current += 1;
            const id = lastIdRef.current;
            isOpenRef.current = true;
            pageBehindRef.current = null;
            attemptRef.current = { target: null };
            entryIdRef.current = crypto.randomUUID();
            changeCreateAnother(false);
            captureCreateDialogOpened(route.record);
            shownRef.current = { form: route.record, openedAt: Date.now() };
            setActive({
                id,
                route,
                params,
                location: { ...to, key: `create-dialog-${id}` },
                round: 0,
                minimised: false,
                entryId: entryIdRef.current,
            });
        },
        [changeCreateAnother]
    );

    const open = useCallback(
        (to: To, state: unknown = null) => {
            const { pathname = '', search = '', hash = '' } = typeof to === 'string' ? parsePath(to) : to;
            const match = matchCreateRoute(pathname);
            if (!match) {
                return false;
            }
            if (!mayOpen(match.route, match.params, user)) {
                return true;
            }
            const next = { route: match.route, params: match.params, to: { pathname, search, hash, state } };
            if (requestRef.current?.hasTyped()) {
                // One creation at a time: the user says what becomes of the one something was typed in.
                ask(next);
            } else {
                end();
                start(next);
            }
            return true;
        },
        [user, ask, end, start]
    );

    const close = useCallback(() => {
        if (askingRef.current) {
            return;
        }
        if (requestRef.current?.hasTyped()) {
            ask(null);
        } else {
            end();
        }
    }, [ask, end]);

    // A record the creation starts from that is missing sends the page to the not-found page, as it does that
    // record's create page. Nothing is left to create from, so the dialog over it closes as its Close does. A
    // minimised dialog is not what the user was looking at, and stays.
    useEffect(() => {
        if (page.pathname === NOT_FOUND_PATH && isOpenRef.current) {
            close();
        }
    }, [page.pathname, close]);

    const answer = (how: 'keep' | 'save' | 'discard') => {
        const next = question?.next ?? null;
        const saveDraft = requestRef.current?.saveDraft;
        setQuestion(null);
        if (how === 'keep') {
            // Back to the form, also from its tab. A creation that waited is dropped.
            reopen();
        } else if (how === 'save' && saveDraft) {
            // The save ends the creation as Save as draft does, or leaves it for the form to show why it was refused.
            changeCreateAnother(false);
            void saveDraft().then((saved) => (saved ? next && start(next) : reopen()));
        } else {
            end();
            if (next) {
                start(next);
            }
        }
    };

    const finish = useCallback(
        (requestId: number, entryId: string, target?: To, options?: CreateDialogFinishOptions) => {
            if (requestId !== lastIdRef.current || entryId !== entryIdRef.current) {
                // A save that answered after its dialog closed: the creation open now is another one.
                return;
            }
            attemptRef.current.target = target ?? null;
            setQuestion(null);
            captureClosed(shownRef.current, options?.draft ? 'kept_as_draft' : 'created');
            if (createAnotherRef.current) {
                // The dialog stays as it is, and `isOpenRef` with it: the popups the finished form goes on to
                // open belong above a dialog that is open.
                attemptRef.current = { target: null };
                const nextEntryId = crypto.randomUUID();
                entryIdRef.current = nextEntryId;
                setActive((current) => current && { ...current, round: current.round + 1, entryId: nextEntryId });
                // The next creation opens with this one's end, or with the tab's reopen while minimised.
                if (shownRef.current?.openedAt != null) {
                    captureCreateDialogOpened(shownRef.current.form);
                    shownRef.current.openedAt = Date.now();
                }
            } else {
                shownRef.current = null;
                isOpenRef.current = false;
                setActive(null);
            }
            // The state is for the page the creation started over. A user who minimised the dialog and went
            // elsewhere is on a page that never asked for it.
            const { pathname, search, hash } = pageRef.current;
            if (options?.pageState !== undefined && (pageBehindRef.current ?? pathname) === pathname) {
                navigate(pathname + search + hash, { replace: true, state: options.pageState });
            }
        },
        [navigate]
    );

    // Bound to the creation it is handed to, which a form's save still holds once that creation is over.
    const activeId = active?.id ?? 0;
    const activeEntryId = active?.entryId ?? '';
    const finishActive = useCallback<CreateDialogRequest['finish']>(
        (target, options) => finish(activeId, activeEntryId, target, options),
        [finish, activeId, activeEntryId]
    );

    // The toast of the mutation that saves the record reads this as it is shown, which is before the form
    // finishes: its Open goes wherever this attempt has finished by the time it is clicked. Nothing is
    // published while the dialog is minimised: the page is in use then, and what it creates is its own.
    useEffect(() => {
        if (!active || active.minimised) {
            return undefined;
        }
        const attempt = attemptRef.current;
        setActiveCreation({
            record: active.route.record,
            open:
                popups > 0
                    ? null
                    : () => {
                          if (attempt.target !== null) {
                              navigate(attempt.target);
                          }
                      },
        });
        return () => setActiveCreation(null);
    }, [active, popups, navigate]);

    const opener = useMemo<CreateDialogOpener>(() => ({ open }), [open]);

    return (
        <CreateDialogOpenerProvider value={opener}>
            <CreateDialogSavedListProvider value={showSaved}>
                <CreateDialogMinimisedProvider value={active?.minimised ?? false}>
                    <CreateDialogOpenProvider value={active !== null && !active.minimised}>
                        {children}
                    </CreateDialogOpenProvider>
                </CreateDialogMinimisedProvider>
            </CreateDialogSavedListProvider>
            {active ? (
                <CreateDialogRequestProvider
                    key={active.id}
                    ref={requestRef}
                    record={active.route.record}
                    location={active.location}
                    params={active.params}
                    sourceId={sourceIdOf(active)}
                    onClose={close}
                    onFinish={finishActive}
                    isOpen={isOpen}
                    createAnother={createAnother}
                    onCreateAnotherChange={changeCreateAnother}
                    minimised={active.minimised}
                    onMinimise={minimise}
                >
                    {/* The dialog's frame and its tab's share an id within the creation's own group, so the next
                        creation never grows out of this one's tab. */}
                    <LayoutGroup id={`${layoutGroupId}${active.id}`}>
                        <CreateDialogFrame>
                            <BodyTop round={active.round} />
                            {/* Mounted afresh for each round: the form works its defaults out again from the request. */}
                            <Fragment key={active.round}>{active.route.element}</Fragment>
                        </CreateDialogFrame>
                        <CreateDialogTab onReopen={reopen} />
                    </LayoutGroup>
                    {question ? (
                        <CreateDialogQuestion
                            next={question.next?.route.record ?? null}
                            onKeep={() => answer('keep')}
                            onSave={() => answer('save')}
                            onDiscard={() => answer('discard')}
                        />
                    ) : null}
                </CreateDialogRequestProvider>
            ) : null}
            {showsSaved ? <SavedForLaterList onClose={() => setShowsSaved(false)} /> : null}
        </CreateDialogOpenerProvider>
    );
}
